import { useEffect, useRef } from 'react'

import { ScrollToBottomButton } from '@ui/components'
import { useInterval } from '@utils/frontend'

import { useChatContext } from './ChatProvider'
import EmptyChat from './components/EmptyChat'

export interface LazyLoadingParams {
	readonly isLoadingMoreMessages: boolean;
	readonly oldHeightBeforeLazyLoading: number;
}

interface ContentProps {
	readonly children: React.ReactNode | React.ReactNode[];
	readonly lazyLoadingParams?: LazyLoadingParams | undefined;
	readonly setLazyLoadingParams?: ((params: LazyLoadingParams) => void) | undefined;
}

export default function Content({ children, lazyLoadingParams, setLazyLoadingParams }: ContentProps) {
	const { type } = useChatContext()

	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (ref.current && lazyLoadingParams) {
			const { isLoadingMoreMessages, oldHeightBeforeLazyLoading } = lazyLoadingParams
			const { scrollHeight } = ref.current

			if (!isLoadingMoreMessages && oldHeightBeforeLazyLoading !== 0) {
			// Calculate the difference in scrollHeight with the loading spinner for smoothness
				const heightDiff = scrollHeight - oldHeightBeforeLazyLoading - 96

				// Scroll to the old position
				ref.current.scrollTo({ top: heightDiff })
			}
		}
	}, [lazyLoadingParams, ref])

	// Display scroll to bottom button when user scrolls up
	const interval = useInterval(() => {
		function handleScroll() {
			if (ref.current) {
				const { scrollTop, scrollHeight } = ref.current

				if (scrollTop === 0 && lazyLoadingParams) {
					setLazyLoadingParams?.({
						isLoadingMoreMessages: true,
						oldHeightBeforeLazyLoading: scrollHeight
					})
				}
			}
		}

		if (ref.current) {
			ref.current.addEventListener('scroll', handleScroll)
			window.clearInterval(interval.current!)
		}

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, 100)

	const noChildren = !children // if no children
		|| (Array.isArray(children) && !children.length) // or if children is an empty array
		|| (Array.isArray(children) && children.every(child => { // or if all children are falsy
			if (Array.isArray(child)) {	// including nested arrays
				return !child.length
			}

			return !child
		}))

	if (noChildren && type === 'chat') {
		return <EmptyChat />
	}

	return (
		<div
			className={ `relative flex-1 overflow-hidden ${type === 'chatbot' ? 'pt-2' : ''}` }>
			<div className="h-full relative">
				<div className="w-full h-full flex">
					<div
						ref={ ref }
						className="w-full h-full overflow-y-auto no-scrollbar"
					>
						<div
							className="h-full content-start items-center relative gap-4 px-8 w-full"
						>
							{ children }
						</div>
					</div>
				</div>
			</div>
			<ScrollToBottomButton
				origin="chat"
				parentRef={ ref }
			/>
		</div>
	)
}

